import { useEffect, useMemo, useRef, useState } from 'react'
import { fbt, useLocaleContext } from 'fbtee'
import { Search, X } from 'lucide-react'
import { clearSearchLabel } from '../../../lib/commonStrings'
import { Input } from '../../../components/ui/input'
import { LoadingLabel } from '../../../components/LoadingLabel'
import { formatDayShort } from '../../../lib/formatDate'
import { cn } from '../../../lib/utils'
import { setLabel } from '../../../lib/setLabel'
import { setSortKey } from '@shared/dayCutoff'
import type { Set as FestivalSet, Stage, Artist } from '../../../types'
import { useDayCutoff } from '../../../contexts/DayCutoffContext'
import { useTimeFormat } from '../../../hooks/useTimeFormat'

const MAX_RESULTS = 20

// Search box for the schedule. Matches across ALL days (not just the active
// tab), then hands the chosen set to onSelect — EventPage turns that into a
// ?set= deep-link that switches day + flashes the block (see scheduleSetPath).
export function ScheduleSearch({
  sets: allSets,
  loading,
  artists,
  stages,
  multiDay,
  onSelect,
}: {
  /** Every timed set for the event, already loaded by the page. */
  sets: FestivalSet[]
  loading: boolean
  artists: Record<string, Artist>
  stages: Stage[]
  multiDay: boolean
  onSelect: (set: FestivalSet) => void
}) {
  const cutoff = useDayCutoff()
  const { locale } = useLocaleContext()
  const { formatClock } = useTimeFormat()
  const [queryText, setQueryText] = useState('')
  // -1 = nothing highlighted. Stays that way until the user arrows, so a mobile
  // user who only taps never sees a highlight.
  const [activeIndex, setActiveIndex] = useState(-1)
  const listRef = useRef<HTMLDivElement>(null)

  const stageName = (id?: string | null) => stages.find(s => s.id === id)?.name


  const needle = queryText.trim().toLowerCase()
  const matches = useMemo(() => {
    if (!needle) return []
    return allSets
      .filter(set => {
        const hay = [...set.artistIds.map(id => artists[id]?.name ?? ''), set.title ?? '']
          .join(' ')
          .toLowerCase()
        return hay.includes(needle)
      })
      .sort((a, b) => setSortKey(a, cutoff).localeCompare(setSortKey(b, cutoff)))
      .slice(0, MAX_RESULTS)
  }, [needle, allSets, artists, cutoff])

  // Keyboard nav (desktop): clamp the highlighted row to the current matches so
  // it stays valid as the list shrinks while typing.
  const activeIdx = matches.length === 0 ? -1 : Math.min(activeIndex, matches.length - 1)

  // Keep the highlighted row scrolled into view as the user arrows through.
  useEffect(() => {
    const el = listRef.current?.children[activeIdx] as HTMLElement | undefined
    el?.scrollIntoView({ block: 'nearest' })
  }, [activeIdx])

  const select = (set: FestivalSet) => {
    onSelect(set)
    setQueryText('')
    setActiveIndex(-1)
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      setQueryText('')
      setActiveIndex(-1)
      return
    }
    if (matches.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex(Math.min(matches.length - 1, activeIdx + 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex(Math.max(0, activeIdx - 1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const set = matches[activeIdx]
      if (set) select(set)
    }
  }

  return (
    <div className="relative z-45 shrink-0">
      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground pointer-events-none" />
      <Input
        value={queryText}
        onChange={e => {
          setQueryText(e.target.value)
          setActiveIndex(-1)
        }}
        onKeyDown={onKeyDown}
        placeholder={fbt('Search artists or sets', 'Placeholder for the schedule search box')}
        className="pl-8 pr-8"
        aria-label={fbt('Search the schedule', 'Accessible label for the schedule search box')}
      />
      {queryText && (
        <button
          type="button"
          onClick={() => setQueryText('')}
          aria-label={clearSearchLabel()}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground cursor-pointer"
        >
          <X className="size-3.5" />
        </button>
      )}

      {needle && (
        <div
          ref={listRef}
          role="listbox"
          className="absolute z-45 left-0 right-0 mt-1 max-h-72 overflow-auto rounded-lg border bg-popover shadow-md"
        >
          {loading && allSets.length === 0 ? (
            <div className="p-3">
              <LoadingLabel>
                {fbt('Loading schedule…', 'Loading state while schedule search data loads')}
              </LoadingLabel>
            </div>
          ) : matches.length === 0 ? (
            <p className="p-3 text-sm text-muted-foreground">
              <fbt desc="Empty state when no sets match the schedule search query">
                No sets match your search.
              </fbt>
            </p>
          ) : (
            matches.map((set, i) => {
              const label = setLabel(set, artists)
              return (
              <button
                key={set.id}
                type="button"
                role="option"
                aria-selected={i === activeIdx}
                onClick={() => select(set)}
                onMouseEnter={() => setActiveIndex(i)}
                className={cn(
                  'flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left cursor-pointer border-b border-border/40 last:border-b-0',
                  i === activeIdx && 'bg-accent/30',
                )}
              >
                <span className="text-sm font-medium leading-tight">{label.name}</span>
                <span className="text-xs text-muted-foreground tabular-nums">
                  {label.title && `${label.title} · `}
                  {multiDay && `${formatDayShort(set.day, locale)} · `}
                  {stageName(set.stageId) && `${stageName(set.stageId)} · `}
                  {formatClock(set.startTime)}
                </span>
              </button>
              )
            })
          )}
        </div>
      )}
    </div>
  )
}
